import { useAuth, useAuthUpdate } from "contexts/AuthContext";
import { jwtDecode } from "jwt-decode";
import { Fullscreen } from "lucide-react";
import React, { useEffect, useState } from "react";
import personImg from "../images/person.png";
import logo from "./assets/logo.png";
import { useTranslation } from "react-i18next";
import NotificationBell from "./NotificationBell";

interface User {
    name: string;
    role: string;
    avatar?: string;
    email: string;
}
interface NavItems {
    name: string;
    href: string;
}
type HeaderProps = {
    isInternalUser: boolean;
};
export interface JwtPayload {
    exp?: number;       // Expiration time in seconds
    iat?: number;       // Issued at
    sub?: string;       // Subject (user id, etc.)
    [key: string]: any; // Any additional claims
}


const Header: React.FC<HeaderProps> = ({ isInternalUser }) => {
    const { t, i18n } = useTranslation();
    const contextData = useAuth();
    const [tokenLoaded, setTokenLoaded] = useState(false);
    const updateAuthContext = useAuthUpdate();
    const handleLogout = () => {
        sessionStorage.removeItem("authToken");
        updateAuthContext({
            ...contextData,
            token: "",
        });
        window.location.href = "/login";
    };
    const [logoutBtn, setLogoutBtn] = useState(false);

    const currentUser: User = {
        name: contextData?.user?.full_name ||
            (contextData?.user?.first_name && contextData?.user?.last_name
                ? `${contextData.user.first_name} ${contextData.user.last_name}`
                : "") || "",
        role: contextData?.user?.roles?.length > 1
            ? contextData?.user?.roles[1]
            : contextData?.user?.roles?.length ? contextData.user.roles[0] : "User",
        email: contextData?.user?.user?.email || contextData?.user?.email || "",
        avatar: personImg,
    };

    const navigationItems: NavItems[] = [
        { name: "Home", href: "#home" },
        { name: "About", href: "#about" },
        { name: "Jobs", href: "#jobs" },
        { name: "Request", href: "#request" },
        { name: "FAQ", href: "#faq" },
    ];

    const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedLang = e.target.value;
        i18n.changeLanguage(selectedLang);
        localStorage.setItem('language', selectedLang);
    };

    if (isInternalUser)
        return renderInsideUserHeader(currentUser, handleLogout, logoutBtn, setLogoutBtn, t, i18n, handleLanguageChange);
    return <></>;
};

const renderInsideUserHeader = (user: User, handleLogout: any, logoutBtn: boolean, setLogoutBtn: any, t: any, i18n: any, handleLanguageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void) => (
    <header className="fixed top-0 right-0 left-0 md:left-64 flex items-center justify-between px-4 md:px-6 py-4 bg-white border-b border-gray-200 shadow-sm z-20">
        {/* Title section */}
        <div className="flex items-center gap-3">
            <div className="h-10 border-l-4 border-red-600" />
            <div className="leading-tight">
                <div className="text-lg font-semibold text-gray-800">
                    LRDIFUD Management
                </div>
                <div className="text-sm font-medium text-red-600">
                    B.A.A.W.O. System
                </div>
            </div>
        </div>

        {/* Right user info */}
        <div className="flex gap-4 ml-auto mr-8">
            <select
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-gray-100 text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                onChange={handleLanguageChange}
                value={i18n.language}
            >
                <option value="or">🌐 Afaan Oromoo</option>
                <option value="en">🌐 English</option>
                <option value="am">🌐 አማርኛ</option>
            </select>
            <NotificationBell />
        </div>
        <div className="flex items-center gap-2 relative"  >

            <div className="hidden sm:block text-right" onClick={() => setLogoutBtn(!logoutBtn)}>
                <div className="text-base font-semibold text-[#0A1D39]">{user.name}</div>
                <div className="text-sm font-normal text-[#432101]">{user.role}</div>
            </div>

            <div className="flex gap-3 relative">
                <img
                    src={user.avatar}
                    alt="User Avatar"
                    width={32}
                    height={32}
                    className="object-cover rounded-full"
                />
                <div className="my-auto">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z" fill="#D9D9D9" />
                    </svg>
                </div>
            </div>
            {logoutBtn &&
                <div className="bg-white absolute right-0 top-12 rounded shadow-lg" >
                    <div
                        className="flex flex-row gap-3 items-center cursor-pointer px-3 py-2 rounded font-medium hover:bg-gray-100"
                        onClick={handleLogout}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h6a2 2 0 002-2v-1"
                            />
                        </svg>
                        <div>{t('Logout', 'Logout')}</div>
                    </div>
                </div>
            }
        </div>
        {/* user icon  */}
    </header>
);

const renderStakeholderHeader = (navItems: NavItems[], user: User, handleLogout: any, logoutBtn: boolean, setLogoutBtn: React.Dispatch<React.SetStateAction<boolean>>, t: any) => (
    <header className="fixed z-20 top-0 left-0 right-0 bg-black border-b">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `
            linear-gradient(45deg, transparent 35%, rgba(255, 255, 255, 0.1) 35%, rgba(255, 255, 255, 0.1) 65%, transparent 65%),
            linear-gradient(-45deg, transparent 35%, rgba(255, 255, 255, 0.1) 35%, rgba(255, 255, 255, 0.1) 65%, transparent 65%)
          `,
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 10px 10px",
                }}
            />
        </div>

        {/* Additional diagonal lines for more complexity */}
        <div className="absolute inset-0 opacity-5">
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `
            linear-gradient(30deg, transparent 40%, rgba(255, 255, 255, 0.15) 40%, rgba(255, 255, 255, 0.15) 60%, transparent 60%),
            linear-gradient(-30deg, transparent 40%, rgba(255, 255, 255, 0.15) 40%, rgba(255, 255, 255, 0.15) 60%, transparent 60%)
          `,
                    backgroundSize: "40px 40px",
                    backgroundPosition: "0 0, 20px 20px",
                }}
            />
        </div>

        <div className="px-4 sm:px-6 lg:px-5 relative z-10 my-auto">
            <div className="flex items-center justify-between h-16">
                {/* Logo Section */}
                <div className="flex items-center gap-2">
                    <img
                        src={logo}
                        alt="Logo"
                        className="w-8 h-8 md:w-10 md:h-10 rounded"
                    />
                    <div className="text-xs leading-tight">
                        <div className="text-sm text-white font-semibold">
                            Biiroo Abbaa Alangaa W.O.
                        </div>
                        <div className="text-gray-400 text-[11px] hidden sm:block">
                            ቢሮዎ አባ አላንጋ ወ.ኦ.
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 relative group">
                    {/* Desktop Navigation */}
                    <nav className="hidden space-x-8">
                        {navItems.map((item, index) => (
                            <a
                                key={index}
                                href={item.href}
                                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm tracking-wide"
                            >
                                {item.name}
                            </a>
                        ))}
                    </nav>

                    {/* user icon  */}
                    <div className="flex items-center gap-2 relative" onClick={() => setLogoutBtn(!logoutBtn)} >
                        <div className="hidden sm:block text-right">
                            <div className="text-base font-semibold text-white">{user.name}</div>
                            <div className="text-sm font-normal text-red-600">{user.email}</div>
                        </div>

                        <div className="flex gap-3 relative">
                            <img
                                src={user.avatar}
                                alt="User Avatar"
                                width={32}
                                height={32}
                                className="object-cover rounded-full"
                            />
                            <div className="my-auto">
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z" fill="#D9D9D9" />
                                </svg>
                            </div>
                        </div>
                        {logoutBtn &&
                            <div className="bg-white absolute right-0 top-12 rounded shadow-lg" >
                                <div
                                    className="flex flex-row gap-3 items-center cursor-pointer px-3 py-2 rounded font-medium hover:bg-gray-100"
                                    onClick={handleLogout}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h6a2 2 0 002-2v-1"
                                        />
                                    </svg>
                                    <div>{t('Logout', 'Logout')}</div>
                                </div>
                            </div>
                        }
                    </div>
                    {/* user icon  */}

                </div>
            </div>
        </div>

    </header>
);
export default Header;
